import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { fetchHolders, getLatestWinner } from '../utils/holderService';

const RaffleContext = createContext();

export const useRaffle = () => useContext(RaffleContext);

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

export const RaffleProvider = ({ children }) => {
  const [holders, setHolders] = useState([]);
  const [shrimpWinner, setShrimpWinner] = useState(null);
  const [whaleWinner, setWhaleWinner] = useState(null);
  const [shrimpRecentWinners, setShrimpRecentWinners] = useState([]);
  const [whaleRecentWinners, setWhaleRecentWinners] = useState([]);
  const [legacyRecentWinners, setLegacyRecentWinners] = useState([]);
  const [nextSpinTime, setNextSpinTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all initial data
  const refreshData = useCallback(async () => {
    setLoading(true);
    try {
      // Parallel data fetching
      const [
        holdersData,
        latestShrimpWinner,
        latestWhaleWinner,
        shrimpWinnersRes,
        whaleWinnersRes,
        allWinnersRes,
        nextRaffleRes
      ] = await Promise.all([
        fetchHolders().catch(e => { console.error(e); return []; }),
        getLatestWinner('shrimp').catch(e => { console.error(e); return null; }),
        getLatestWinner('whale').catch(e => { console.error(e); return null; }),
        fetch(`${API_URL}/api/raffle/winners?tier=shrimp`).then(res => res.json()).catch(e => { console.error(e); return []; }),
        fetch(`${API_URL}/api/raffle/winners?tier=whale`).then(res => res.json()).catch(e => { console.error(e); return []; }),
        fetch(`${API_URL}/api/raffle/winners`).then(res => res.json()).catch(e => { console.error(e); return []; }),
        fetch(`${API_URL}/api/raffle/next`).then(res => res.json()).catch(e => { console.error(e); return {}; })
      ]);

      setHolders(holdersData);
      setShrimpWinner(latestShrimpWinner);
      setWhaleWinner(latestWhaleWinner);
      setShrimpRecentWinners(shrimpWinnersRes);
      setWhaleRecentWinners(whaleWinnersRes);
      setLegacyRecentWinners(allWinnersRes.filter(winner => !winner.tier));
      
      if (nextRaffleRes) {
        if (nextRaffleRes.nextDate) {
          setNextSpinTime(new Date(nextRaffleRes.nextDate));
        } else {
          setNextSpinTime(null);
        }
      }
    } catch (err) {
      console.error("Error refreshing raffle data:", err);
      setError("Failed to load raffle data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    refreshData();
    
    // Poll for next raffle time every minute to keep it synced
    const interval = setInterval(async () => {
        try {
            const res = await fetch(`${API_URL}/api/raffle/next`);
            const data = await res.json();
            if (data && data.nextDate) {
                setNextSpinTime(new Date(data.nextDate));
            }
        } catch (e) {
            console.error("Polling failed", e);
        }
    }, 60000);

    return () => clearInterval(interval);
  }, [refreshData]);

  const value = {
    holders,
    shrimpWinner,
    whaleWinner,
    setShrimpWinner,
    setWhaleWinner,
    shrimpRecentWinners,
    whaleRecentWinners,
    legacyRecentWinners,
    nextSpinTime,
    loading,
    error,
    refreshData
  };

  return (
    <RaffleContext.Provider value={value}>
      {children}
    </RaffleContext.Provider>
  );
};
